import { useUpdateVenue } from "@/hooks/use-venue-management";
import { X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

type VenueEditDialogProps = {
  open: boolean;
  onClose: () => void;
  venueId: string;
  currentName: string;
  currentStrategy: string | null;
};

/** venue 編集モーダル */
export const VenueEditDialog = ({
  open,
  onClose,
  venueId,
  currentName,
  currentStrategy,
}: VenueEditDialogProps) => {
  const [name, setName] = useState(currentName);
  const [strategy, setStrategy] = useState(currentStrategy ?? "");
  const nameInputRef = useRef<HTMLInputElement>(null);
  const updateVenue = useUpdateVenue();

  useEffect(() => {
    if (open) {
      setName(currentName);
      setStrategy(currentStrategy ?? "");
      setTimeout(() => nameInputRef.current?.focus(), 100);
    }
  }, [open, currentName, currentStrategy]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [open, handleKeyDown]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;

    updateVenue.mutate(
      {
        id: venueId,
        data: {
          name: trimmedName,
          strategy: strategy.trim() || null,
        },
      },
      { onSuccess: () => onClose() },
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        onKeyDown={() => {}}
        role="presentation"
      />
      <div className="relative mx-4 w-full max-w-sm rounded-lg bg-background p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-foreground">お店を編集</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-muted-foreground hover:bg-muted"
            aria-label="閉じる"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <input
            ref={nameInputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="店名"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <textarea
            value={strategy}
            onChange={(e) => setStrategy(e.target.value)}
            placeholder="戦略メモ（AI 補完のヒントになります）"
            rows={3}
            className="mt-3 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              disabled={!name.trim() || updateVenue.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {updateVenue.isPending ? "更新中..." : "更新"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
